import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Plus, X, Check } from '@/components/icons';
import { supabase } from '@/lib/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface BankAccount {
  cbu: string;
  bank_name: string;
  account_holder: string;
  alias?: string;
  primary?: boolean;
}

interface BankAccountsManagerProps {
  mode: 'store' | 'user';
  entityId: string; // storeId or userId
  onSaved?: () => void;
}

const BankAccountsManager: React.FC<BankAccountsManagerProps> = ({ mode, entityId, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newAccount, setNewAccount] = useState<BankAccount>({
    cbu: '',
    bank_name: '',
    account_holder: '',
    alias: '',
    primary: false,
  });
  const [isAddingNew, setIsAddingNew] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadBankAccounts();
  }, [entityId, mode]);

  const loadBankAccounts = async () => {
    setLoading(true);
    try {
      const table = mode === 'store' ? 'stores' : 'profiles';
      const { data, error } = await supabase
        .from(table)
        .select('bank_accounts')
        .eq('id', entityId)
        .single();
      if (error) {
        if (error.code === 'PGRST204' || error.message?.includes('bank_accounts')) {
          setBankAccounts([]);
          return;
        }
        throw error;
      }
      const accounts = (data?.bank_accounts as BankAccount[]) || [];
      setBankAccounts(accounts);
    } catch (e: any) {
      console.error('Error loading bank accounts:', e);
      if (!e?.message?.includes('bank_accounts') && e?.code !== '42703') {
        toast({
          title: 'Error',
          description: 'No pudimos cargar las cuentas bancarias.',
          variant: 'destructive',
        });
      }
      setBankAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const saveBankAccounts = async (accounts: BankAccount[]) => {
    setSaving(true);
    try {
      const table = mode === 'store' ? 'stores' : 'profiles';
      const { error } = await supabase
        .from(table)
        .update({ bank_accounts: accounts })
        .eq('id', entityId);
      if (error) throw error;
      setBankAccounts(accounts);
      onSaved?.();
    } catch (e: any) {
      console.error('Error saving bank accounts:', e);
      toast({
        title: 'Error',
        description: e?.message || 'No pudimos guardar las cuentas.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddAccount = async () => {
    if (!newAccount.cbu.trim() || !newAccount.bank_name.trim() || !newAccount.account_holder.trim()) {
      toast({
        title: 'Error',
        description: 'Completá todos los campos.',
        variant: 'destructive',
      });
      return;
    }
    const updatedAccounts = [...bankAccounts, newAccount];
    await saveBankAccounts(updatedAccounts);
    setNewAccount({ cbu: '', bank_name: '', account_holder: '', alias: '', primary: false });
    setIsAddingNew(false);
  };

  const handleUpdateAccount = async () => {
    if (editingIndex === null) return;
    if (!newAccount.cbu.trim() || !newAccount.bank_name.trim() || !newAccount.account_holder.trim()) {
      toast({
        title: 'Error',
        description: 'Completá todos los campos.',
        variant: 'destructive',
      });
      return;
    }
    const updatedAccounts = [...bankAccounts];
    updatedAccounts[editingIndex] = newAccount;
    await saveBankAccounts(updatedAccounts);
    setEditingIndex(null);
    setNewAccount({ cbu: '', bank_name: '', account_holder: '', alias: '', primary: false });
  };

  const handleDeleteAccount = async (index: number) => {
    const updatedAccounts = bankAccounts.filter((_, i) => i !== index);
    await saveBankAccounts(updatedAccounts);
  };

  const handleEditAccount = (index: number) => {
    setNewAccount(bankAccounts[index]);
    setEditingIndex(index);
  };

  const handleSetPrimary = async (index: number) => {
    const selectedAccount = bankAccounts[index];
    const otherAccounts = bankAccounts.filter((_, i) => i !== index).map(acc => ({ ...acc, primary: false }));
    const updatedAccounts = [
      { ...selectedAccount, primary: true },
      ...otherAccounts
    ];
    await saveBankAccounts(updatedAccounts);
  };

  const handleRemoveAccount = (index: number) => {
    const updatedAccounts = bankAccounts.filter((_, i) => i !== index);
    saveBankAccounts(updatedAccounts);
  };

  const handleCancel = () => {
    setIsAddingNew(false);
    setEditingIndex(null);
    setNewAccount({ cbu: '', bank_name: '', account_holder: '', alias: '', primary: false });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-3">
          <Skeleton className="h-6 w-48" />
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="p-3 sm:p-4 bg-muted/50 rounded-lg space-y-3">
                <Skeleton className="h-5 w-32" />
                <div className="space-y-2.5">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20 rounded-xl p-4 space-y-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Tus cuentas bancarias</p>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-primary">{bankAccounts.length}</span>
          <span className="text-xs text-muted-foreground">{bankAccounts.length === 1 ? 'cuenta' : 'cuentas'} registrada{bankAccounts.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Existing Bank Accounts List */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Detalle</h3>
        {bankAccounts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">No tienes cuentas bancarias registradas.</p>
            <p className="text-xs mt-1">Agrega una para recibir pagos.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {bankAccounts.map((account, index) => (
              <Card key={index} className={cn('border transition-all hover:border-primary/40', account.primary && 'border-primary/60 bg-primary/5')}>
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Titular</p>
                        <p className="font-semibold text-foreground">{account.account_holder}</p>
                      </div>
                      {account.primary && (
                        <div className="flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-950 px-2.5 py-1 rounded-full ml-2 shrink-0">
                          <Check className="h-3 w-3" />
                          Principal
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Banco</p>
                        <p className="font-medium text-sm text-foreground">{account.bank_name}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">CBU</p>
                        <p className="text-xs font-mono text-foreground">{account.cbu}</p>
                      </div>
                    </div>
                    {account.alias && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Alias</p>
                        <p className="text-sm text-foreground italic">{account.alias}</p>
                      </div>
                    )}
                    <div className="flex gap-2 pt-2 border-t">
                      {!account.primary && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetPrimary(index)}
                          disabled={saving}
                          className="flex-1"
                        >
                          Hacer principal
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditAccount(index)}
                        disabled={saving}
                        className="flex-1"
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveAccount(index)}
                        disabled={saving}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Form */}
      {(isAddingNew || editingIndex !== null) && (
        <Card className="border-primary/60 bg-primary/5">
          <CardContent className="pt-4 space-y-4">
            <h4 className="font-semibold text-foreground">
              {editingIndex !== null ? 'Editar cuenta' : 'Agregar nueva cuenta'}
            </h4>
            
            <div>
              <Label htmlFor="account_holder" className="text-xs font-semibold uppercase tracking-wide">Titular de la cuenta</Label>
              <Input
                id="account_holder"
                value={newAccount.account_holder}
                onChange={(e) => setNewAccount({ ...newAccount, account_holder: e.target.value })}
                placeholder="Tu nombre completo"
                disabled={saving}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="bank_name" className="text-xs font-semibold uppercase tracking-wide">Banco</Label>
              <Input
                id="bank_name"
                value={newAccount.bank_name}
                onChange={(e) => setNewAccount({ ...newAccount, bank_name: e.target.value })}
                placeholder="Ej: Banco de la Nación"
                disabled={saving}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="cbu" className="text-xs font-semibold uppercase tracking-wide">CBU</Label>
              <Input
                id="cbu"
                value={newAccount.cbu}
                onChange={(e) => setNewAccount({ ...newAccount, cbu: e.target.value })}
                placeholder="Código Bancario Uniforme (22 dígitos)"
                disabled={saving}
                className="mt-1.5 font-mono"
              />
            </div>

            <div>
              <Label htmlFor="alias" className="text-xs font-semibold uppercase tracking-wide">Alias (opcional)</Label>
              <Input
                id="alias"
                value={newAccount.alias || ''}
                onChange={(e) => setNewAccount({ ...newAccount, alias: e.target.value })}
                placeholder="Mi banco principal"
                disabled={saving}
                className="mt-1.5"
              />
            </div>

            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={editingIndex !== null ? handleUpdateAccount : handleAddAccount}
                disabled={saving}
                className="flex-1"
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingIndex !== null ? 'Guardar cambios' : 'Agregar cuenta'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Button */}
      {!isAddingNew && editingIndex === null && (
        <Button
          onClick={() => setIsAddingNew(true)}
          disabled={saving}
          className="w-full bg-primary hover:bg-primary/90"
        >
          <Plus className="h-4 w-4 mr-2" />
          Agregar cuenta bancaria
        </Button>
      )}
    </div>
  );
};

export default BankAccountsManager;
